import { useState, useEffect, useContext } from 'react';
import { Box, Grid, Typography, Divider, Switch, Tooltip } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { getNotificationGroups } from './configurationNotificationGroups';

type Device = {
    id: number;
    name: string;
};

export default function ConfigurationNotifications(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [devices, setDevices] = useCrud<Device[]>([]);
    const [registeredDevices, setRegisteredDevices] = useState<number[]>(
        localStorage.getItem('registeredDevices')
            ? JSON.parse(localStorage.getItem('registeredDevices') || '[]')
            : [],
    );
    const [updatedNotifications, setUpdatedNotifications] =
        useState<boolean>(false);

    // Load devices
    useEffect(() => {
        cxt?.api('GET', `/scs/${cxt.sc?.id}/devices`, {
            success: (response: { count: number; rows: Device[] }) => {
                if (response.count > 0) {
                    setDevices(response.rows);
                }
            },
        });
    }, []);

    // Save devices ids to localStorage
    const updateRegisteredDevices = (): void => {
        localStorage.setItem(
            'registeredDevices',
            JSON.stringify(registeredDevices),
        );
    };

    // Trigger saving device ids when they change
    useEffect(() => {
        updateRegisteredDevices();
    }, [registeredDevices]);

    // Register device id for this user
    const registerDevice = (id: number, enabled: boolean): void => {
        if (enabled) {
            setRegisteredDevices([...registeredDevices, id]);
        } else {
            setRegisteredDevices(
                registeredDevices.filter((device: any) => device !== id),
            );
        }
        setUpdatedNotifications(true);
        cxt?.sendMessageToSocket({
            data: {
                deviceId: id,
                enabled,
            },
        } as any);
    };

    const notConfGroups = getNotificationGroups(t);

    // -----| Render |-----
    return (
        <>
            {devices.length > 0 && (
                <Box>
                    <Box mb={2} display="flex">
                        <span>
                            {t('page.sc.configuration.notifications.realTime')}
                        </span>
                        <Tooltip
                            arrow
                            placement="top"
                            title={t('configuration.localConfigNote')}
                        >
                            <HelpOutline />
                        </Tooltip>
                    </Box>
                    <div>
                        {updatedNotifications && (
                            <Box mb={2} display="flex">
                                <span>
                                    {t(
                                        'page.sc.configuration.notifications.realTimeRefreshPage',
                                    )}
                                </span>
                            </Box>
                        )}
                    </div>

                    <div>
                        {devices.map((device: any) => (
                            <Box
                                key={device?.id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <div>
                                    <Typography variant="body1">
                                        {device.name}
                                    </Typography>
                                </div>
                                <Switch
                                    checked={
                                        registeredDevices.find(
                                            (id: any) => id === device.id,
                                        ) !== undefined
                                    }
                                    onChange={(ev: any) =>
                                        registerDevice(
                                            device.id,
                                            ev.target.checked,
                                        )
                                    }
                                />
                            </Box>
                        ))}
                    </div>
                </Box>
            )}
            <br />
            <Divider />
            <br />
            <Box>
                <Grid container spacing={3}>
                    <Grid size={{ md: 8 }}>{t('Description')}</Grid>
                    <Grid size={{ md: 2 }}>{t('Email')}</Grid>
                </Grid>
                {notConfGroups.map((notConfGroup: any) => (
                    <Box key={notConfGroup.title} mt={2}>
                        <Typography variant="h5">
                            {notConfGroup.title}
                        </Typography>
                        <Box mb={1}>
                            <Divider />
                        </Box>
                        <Box mt={1}>
                            {notConfGroup.notifications.map((not: any) => (
                                <Grid key={not?.name} container spacing={3}>
                                    <Grid size={{ md: 8 }}>{not.name}</Grid>
                                    <Grid size={{ md: 2 }}>
                                        {not.email && <DoneIcon />}
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );
}
